// src/components/SignatureComponent.jsx
import React, { useRef, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  IconButton,
} from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PropTypes from "prop-types";

function SignatureComponent({
  label,
  signatureData,
  onSignatureChange,
  onRemove,
  isEditable,
}) {
  const sigCanvas = useRef(null);

  // Clear canvas when signatureData is cleared externally
  useEffect(() => {
    if (!signatureData && sigCanvas.current) {
      sigCanvas.current.clear();
    }
  }, [signatureData]);

  // Auto-capture signature data after each stroke ends
  const handleStrokeEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataURL = sigCanvas.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
      onSignatureChange(dataURL);
    }
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    onSignatureChange("");
  };

  return (
    <Box
      sx={{
        border: "1px solid #ccc",
        borderRadius: 1,
        padding: 2,
        marginTop: 2,
      }}
    >
      <Typography variant="subtitle1" gutterBottom>
        {label}
      </Typography>

      {isEditable ? (
        <>
          {signatureData ? (
            // Show captured signature with option to redo
            <>
              <Box
                sx={{
                  width: "80%",
                  maxWidth: 500,
                  mx: "auto",
                  border: "1px solid #000",
                  height: 200,
                  position: "relative",
                  marginBottom: 2,
                }}
              >
                <img
                  src={signatureData}
                  alt="User Signature"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
                <IconButton
                  aria-label="remove signature"
                  onClick={onRemove}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "rgba(255,255,255,0.7)",
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Box>
              {/* Captured indicator */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="caption" color="success.main">
                  Signature captured — will be saved on submit
                </Typography>
              </Box>
              <Button variant="outlined" size="small" onClick={clearSignature}>
                Clear &amp; Redo
              </Button>
            </>
          ) : (
            // Show canvas — auto-captures on each stroke end
            <>
              <SignatureCanvas
                ref={sigCanvas}
                penColor="black"
                onEnd={handleStrokeEnd}
                canvasProps={{
                  height: 200,
                  className: "sigCanvas",
                  style: { border: "1px solid #000", width: "80%" },
                }}
              />
              <Box sx={{ marginTop: 1 }}>
                <Button variant="outlined" size="small" onClick={clearSignature}>
                  Clear
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Sign above — your signature is captured automatically
              </Typography>
            </>
          )}
        </>
      ) : (
        <>
          {signatureData ? (
            <img
              src={signatureData}
              alt="User Signature"
              style={{ maxWidth: "100%", border: "1px solid #ccc" }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              No signature provided.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

SignatureComponent.propTypes = {
  label: PropTypes.string.isRequired,
  signatureData: PropTypes.string,
  onSignatureChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired,
};

SignatureComponent.defaultProps = {
  signatureData: "",
};

export default SignatureComponent;